import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { CoreTracking } from "../../../../models/CoreTracking";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModalComponent from '../../../misc/ModalComponent';
import { ClearMapEntraIdUserTracking, MapEntraIdUserTracking } from '../../../../store/TrackingOperationAction';
import StatusCode from '../../../../util/StatusCode';
import Loading from '../../../misc/Loading';
import { CoreEntraIdUserTrackingConflict, CoreEntraIdUserTrackingMapping } from "../../../../models/CoreTrackingOperation";
import { RequestState } from "../../../../models/RequestState";
import CoreApiError from "../../../misc/CoreApiError";
import EntraIdUserTrackingOperationsValidationResult from "./EntraIdUserTrackingOperationsValidationResult";
import { EntraIdUserTrackingMetadata } from "../../../../models/TrackingMetadata";
import { useTranslation } from 'react-i18next';
import { RootState } from "../../../../store/store";



interface MapEntraIdUserTrackingWizardProps {
    tracking: CoreTracking
    close: (success: boolean) => void
}

const MapEntraIdUserTrackingWizard: React.FC<MapEntraIdUserTrackingWizardProps> = (props) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(['schoolAdministration', 'common']);    
    const metadata = JSON.parse(props.tracking.metadata) as EntraIdUserTrackingMetadata;
    const [wizardStep, setWizardStep] = useState<'input' | 'verify' | 'done'>('input');
    const [update, setUpdate] = useState<CoreEntraIdUserTrackingMapping>({ entraIdId: '', userPrincipalName: '', primarySmtpAddress: '' });
    const mapEntraIdUserTracking = useSelector<RootState, RequestState<CoreEntraIdUserTrackingConflict[]>>(state => state.trackingOperation.mapEntraIdUserTracking)
    const initial: CoreEntraIdUserTrackingMapping = {
        entraIdId: props.tracking.sourceSystemId,
        userPrincipalName: metadata.UserPrincipalName.Value,
        primarySmtpAddress: metadata.PrimaryEmail,
    };

    const updateEntraIdId: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setUpdate({
            ...update,
            entraIdId: e.target.value,
        });
    }

    const updateUserPrincipalName: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setUpdate({
            ...update,
            userPrincipalName: e.target.value,
        });
    }

    const updatePrimarySmtpAddress: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        setUpdate({
            ...update,
            primarySmtpAddress: e.target.value,
        });
    }

    const submitInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        MapEntraIdUserTracking(update, props.tracking.trackingId, true)(dispatch);
        setWizardStep('verify');
    }

    const confirmInput: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        MapEntraIdUserTracking(update, props.tracking.trackingId, false)(dispatch);
        setWizardStep('done');
    }

    const close = (success: boolean) => {
        ClearMapEntraIdUserTracking()(dispatch);
        setWizardStep('input');
        setUpdate({ entraIdId: '', userPrincipalName: '', primarySmtpAddress: '' });
        props.close(success);
    }

    switch (wizardStep) {
        case 'input': {
            if (isCurrentlyLifecycleManaged(props.tracking)) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><FontAwesomeIcon title={t('status.error', {ns: 'common'})} color="red" icon={['fas', 'triangle-exclamation']} />{t('trackingOperations.general.lifeCycleError', {ns: 'schoolAdministration'})}</ModalComponent>;
            }

            return <ModalComponent
                isOpen={true}
                toggleModal={() => close(false)}
                header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                update={submitInput}
                disabled={!isValid(update) || !hasChanges(update, initial)}
                confirm={t('action.validate', {ns: 'common'})}
                cancel={t('action.cancel', {ns: 'common'})}>
                <Form>
                    <FormGroup key="entraIdAccountId">
                        <Label for="entraIdAccountId">{t('trackingOperations.entraId.edit.entraIdAccountId', {ns: 'schoolAdministration'})}</Label>
                        <Input
                            type="text"
                            name="entraIdAccountId"
                            id="entraIdAccountId"
                            placeholder={t('trackingOperations.general.identifierPlaceholder', {ns: 'schoolAdministration'})}
                            autoComplete="off"
                            invalid={update.entraIdId === ''}
                            value={update.entraIdId}
                            onChange={updateEntraIdId} />
                        <FormFeedback>{t('trackingOperations.entraId.edit.entraIdAccountIdFormFeedback', {ns: 'schoolAdministration'})}</FormFeedback>
                    </FormGroup>
                    <FormGroup key="entraIdUserPrincipalName">
                        <Label for="entraIdUserPrincipalName">{t('trackingOperations.entraId.edit.upnLabel', {ns: 'schoolAdministration'})}</Label>
                        <Input
                            type="text"
                            name="entraIdUserPrincipalName"
                            id="entraIdUserPrincipalName"
                            placeholder={t('trackingOperations.general.emailPlaceholder', {ns: 'schoolAdministration'})}
                            autoComplete="off"
                            invalid={update.userPrincipalName === ''}
                            value={update.userPrincipalName}
                            onChange={updateUserPrincipalName} />
                        <FormFeedback>{t('trackingOperations.entraId.edit.upnFormFeedback', {ns: 'schoolAdministration'})}</FormFeedback>
                    </FormGroup>
                    <FormGroup key="entraIdPrimarySmtpAddress">
                        <Label for="entraIdPrimarySmtpAddress">{t('trackingOperations.entraId.edit.smtpLabel', {ns: 'schoolAdministration'})}</Label>
                        <Input
                            type="text"
                            name="entraIdPrimarySmtpAddress"
                            id="entraIdPrimarySmtpAddress"
                            placeholder={t('trackingOperations.general.emailPlaceholder', {ns: 'schoolAdministration'})}
                            autoComplete="off"
                            invalid={update.primarySmtpAddress === ''}
                            value={update.primarySmtpAddress}
                            onChange={updatePrimarySmtpAddress} />
                        <FormFeedback>{t('trackingOperations.entraId.edit.smtpFormFeedback', {ns: 'schoolAdministration'})}</FormFeedback>
                    </FormGroup>
                </Form>
            </ModalComponent>
        }
        case 'verify': {
            if (mapEntraIdUserTracking.code === StatusCode.COMPLETE) {
                if (!hasChanges(update, initial)) {
                    return <ModalComponent
                        isOpen={true}
                        toggleModal={() => close(false)}
                        header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                        cancel={t('action.cancel', {ns: 'common'})}>{t('trackingOperations.general.verifyNoChanges', {ns: 'schoolAdministration'})}</ModalComponent>
                } else if (mapEntraIdUserTracking.data.length === 0) {
                    return <ModalComponent
                        isOpen={true}
                        toggleModal={() => close(false)}
                        header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                        update={confirmInput}
                        confirm={t('action.confirm', {ns: 'common'})}
                        cancel={t('action.cancel', {ns: 'common'})}>
                        <p><FontAwesomeIcon title={t('status.OK', {ns: 'common'})} color="green" icon={['fas', 'check-circle']} />{t('trackingOperations.general.noErrorsConfirm', {ns: 'schoolAdministration'})}</p>
                        {(update.entraIdId !== initial.entraIdId) && <div className="mt-3">
                            <b>{t('trackingOperations.entraId.edit.changeEntraIdId', {ns: 'schoolAdministration'})}</b>
                            <div className="gridList" style={{ gridTemplateColumns: '1fr 7fr' }}>
                                <div>{t('trackingOperations.general.from', {ns: 'schoolAdministration'})}</div>
                                <div>{initial.entraIdId}</div>
                                <div>{t('trackingOperations.general.to', {ns: 'schoolAdministration'})}</div>
                                <div>{update.entraIdId}</div>
                            </div>
                        </div>}
                        {(update.userPrincipalName !== initial.userPrincipalName) && <>
                            <div className="mt-3">
                                <b>{t('trackingOperations.entraId.edit.changeUsername', {ns: 'schoolAdministration'})}</b>
                                <div className="gridList" style={{ gridTemplateColumns: '1fr 7fr' }}>
                                    <div>{t('trackingOperations.general.from', {ns: 'schoolAdministration'})}</div>
                                    <div>{initial.userPrincipalName}</div>
                                    <div>{t('trackingOperations.general.to', {ns: 'schoolAdministration'})}</div>
                                    <div>{update.userPrincipalName}</div>
                                </div>
                            </div>
                        </>}
                        {(update.primarySmtpAddress !== initial.primarySmtpAddress) && <>
                            <div className="mt-3">
                                <b>{t('trackingOperations.entraId.edit.changeSmtp', {ns: 'schoolAdministration'})}</b>
                                <div className="gridList" style={{ gridTemplateColumns: '1fr 7fr' }}>
                                    <div>{t('trackingOperations.general.from', {ns: 'schoolAdministration'})}</div>
                                    <div>{initial.primarySmtpAddress}</div>
                                    <div>{t('trackingOperations.general.to', {ns: 'schoolAdministration'})}</div>
                                    <div>{update.primarySmtpAddress}</div>
                                </div>
                            </div>
                        </>}
                        <p className="mt-3"><FontAwesomeIcon title={t('status.warning', {ns: 'common'})} color="#fc1" icon={['fas', 'triangle-exclamation']} />{t('trackingOperations.general.mapWarningMessage', {ns: 'schoolAdministration'})}</p>
                    </ModalComponent>;
                }

                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <p><FontAwesomeIcon title={t('status.error', {ns: 'common'})} color="red" icon={['fas', 'triangle-exclamation']} />{t('trackingOperations.general.conflictsDiscovered', {ns: 'schoolAdministration'})}</p>
                    <EntraIdUserTrackingOperationsValidationResult entraIdId={update.entraIdId} primarySmtpAddress={update.primarySmtpAddress} userPrincipalName={update.userPrincipalName} conflicts={mapEntraIdUserTracking.data} />
                </ModalComponent>;
            } else if (mapEntraIdUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><CoreApiError  requestState={mapEntraIdUserTracking} /></ModalComponent>
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <Loading />
                </ModalComponent>;
            }
        }
        case 'done': {
            if (mapEntraIdUserTracking.code === StatusCode.COMPLETE) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(true)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.close', {ns: 'common'})}>{t('trackingOperations.general.changeCompleted', {ns: 'schoolAdministration'})}</ModalComponent>;
            } else if (mapEntraIdUserTracking.code === StatusCode.ERROR) {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}><CoreApiError  requestState={mapEntraIdUserTracking} /></ModalComponent>;
            } else {
                return <ModalComponent
                    isOpen={true}
                    toggleModal={() => close(false)}
                    header={t('trackingOperations.entraId.modalTitle', {ns: 'schoolAdministration'})}
                    cancel={t('action.cancel', {ns: 'common'})}>
                    <Loading />
                </ModalComponent>;
            }
        }
    }
}

const hasChanges = (update: CoreEntraIdUserTrackingMapping, initial: CoreEntraIdUserTrackingMapping) => {
    if (update.entraIdId !== initial.entraIdId) {
        return true;
    }

    if (update.primarySmtpAddress !== initial.primarySmtpAddress) {
        return true;
    }

    if (update.userPrincipalName !== initial.userPrincipalName) {
        return true;
    }

    return false;
}

const isValid = (update: CoreEntraIdUserTrackingMapping) => update.entraIdId !== '' && update.primarySmtpAddress !== '' && update.userPrincipalName !== '';

const isCurrentlyLifecycleManaged = (trackig: CoreTracking) => trackig.active && !trackig.deleted && (trackig.inactivated || trackig.archived);

export default MapEntraIdUserTrackingWizard;
